// src/pages/EventDetailsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';

const EventDetailsPage = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/events/${eventId}`);
      const data = await response.json();

      if (data.success) {
        setEvent(data.data.event);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch event details');
      console.error('Error fetching event details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = () => {
    // Members-only events require login; open events accept everyone
    if (event?.members_only && !user) {
      alert('This event is for ARCA members only. Please login or become a member to register.');
      navigate('/login');
      return;
    }

    // Multi-step registration flow: Form -> CPD (conditional) -> Review -> Payment -> Confirmation
    navigate(`/events/${eventId}/register`);
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/New_York' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const est = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' });
    const cst = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' });
    const gmt = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    return `${est} EST / ${cst} CST / ${gmt} GMT`;
  };

  if (loading) {
    return (
      <PublicLayout user={user}>
        <div className="container py-5">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-3 text-muted">Loading event details...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout user={user}>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-5 text-center">
                  <div className="text-danger mb-3">
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px' }}></i>
                  </div>
                  <h3 className="h4 fw-bold text-dark mb-2">Error</h3>
                  <p className="text-muted mb-4">{error}</p>
                  <button
                    onClick={() => navigate('/events')}
                    className="btn btn-success"
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Events
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!event) {
    return (
      <PublicLayout user={user}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">Event not found</h3>
              <p className="mt-1 text-gray-500">The event you're looking for doesn't exist or is no longer available.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/events')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Back to Events
                </button>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout user={user}>
      <div className="container py-5">
        {/* Back button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/events')}
            className="btn btn-link text-success text-decoration-none p-0"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Events
          </button>
        </div>

        {/* Event Header */}
        <div className="card border-0 shadow-sm mb-4">
          {event.image_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${event.image_url}`}
              alt={event.name}
              className="card-img-top"
              style={{ width: '100%', height: 'auto' }}
            />
          ) : (
            <div className="card-img-top d-flex align-items-center justify-content-center"
              style={{
                height: '300px',
                background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)'
              }}>
              <i className="fas fa-calendar-alt text-white" style={{ fontSize: '60px' }}></i>
            </div>
          )}

          <div className="card-body p-3 p-md-4">
            <div className="row">
              <div className="col-12 col-md-8 mb-3 mb-md-0">
                <h1 className="h3 h-md-2 fw-bold text-dark mb-3">{event.name}</h1>
                <div className="d-flex flex-wrap gap-2 mb-3 mb-md-4">
                  <span className="badge text-white" style={{ backgroundColor: '#6B8E23' }}>
                    {event.event_type}
                  </span>
                  <span className={`badge ${event.category === 'Paid'
                    ? 'bg-warning text-dark'
                    : 'bg-info text-white'
                    }`}>
                    {event.category}
                  </span>
                  <span className="badge bg-secondary">
                    {event.medium}
                  </span>
                </div>
                <p className="text-muted mb-0">
                  {event.description || 'No description available for this event.'}
                </p>
              </div>

              <div className="col-12 col-md-4 text-center text-md-end mt-3 mt-md-0">
                {registrationSuccess ? (
                  <div className="alert alert-success" role="alert">
                    <i className="fas fa-check-circle me-2"></i>
                    Successfully registered!
                  </div>
                ) : event.registration_link ? (
                  /* External registration link (Free Online events with Google Form etc.) */
                  <div>
                    <a
                      href={event.registration_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-lg fw-bold text-white w-100"
                      style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                    >
                      <i className="fas fa-external-link-alt me-2"></i>
                      Register Now
                    </a>
                    <small className="d-block mt-2 text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Opens registration form
                    </small>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={handleRegister}
                      disabled={isRegistering}
                      className="btn btn-lg fw-bold text-white w-100"
                      style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                    >
                      {isRegistering ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Registering...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus me-2"></i>
                          Register for Event
                        </>
                      )}
                    </button>
                    {event.members_only ? (
                      <small className="d-block mt-2 text-muted">
                        <i className="fas fa-lock me-1"></i>
                        Members only
                      </small>
                    ) : (
                      <small className="d-block mt-2 text-muted">
                        <i className="fas fa-globe me-1"></i>
                        Open to all
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="row g-3 g-md-4">
          <div className="col-12 col-lg-8 order-2 order-lg-1">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3 p-md-4">
                <h2 className="h4 fw-bold text-dark mb-4">Event Details</h2>
                <div className="row g-3 g-md-4">
                  <div className="col-12">
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <i className="fas fa-calendar-alt text-success" style={{ fontSize: '24px' }}></i>
                      </div>
                      <div className="ms-3">
                        <h3 className="h6 fw-bold text-dark">Date & Time</h3>
                        <p className="text-muted mb-1">{formatDate(event.start_date)}</p>
                        <p className="text-muted mb-0">{formatTime(event.start_date)} (Duration: {event.duration} minutes)</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <i className="fas fa-map-marker-alt text-success" style={{ fontSize: '24px' }}></i>
                      </div>
                      <div className="ms-3">
                        <h3 className="h6 fw-bold text-dark">Location</h3>
                        <p className="text-muted mb-0">
                          {event.medium === 'online'
                            ? event.meeting_link
                              ? <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="text-success">Join Online Meeting</a>
                              : 'Online (Meeting link will be provided via email)'
                            : event.location || 'Location details will be provided via email'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <i className="fas fa-clock text-success" style={{ fontSize: '24px' }}></i>
                      </div>
                      <div className="ms-3">
                        <h3 className="h6 fw-bold text-dark">Duration</h3>
                        <p className="text-muted mb-0">{event.duration} minutes</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4 order-1 order-lg-2">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3 p-md-4">
                <h2 className="h4 fw-bold text-dark mb-4">Registration Info</h2>
                <div className="mb-3">
                  <h3 className="h6 fw-bold text-dark">Pricing</h3>
                  {event.category === 'Free' ? (
                    <p className="text-muted mb-0">Free</p>
                  ) : (
                    <div className="d-grid gap-2">
                      {event.member_price && (
                        <div className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: '#f0f8f0' }}>
                          <span className="small">Member Price:</span>
                          <span className="fw-bold" style={{ color: '#6B8E23' }}>${parseFloat(event.member_price).toFixed(2)}</span>
                        </div>
                      )}
                      {event.non_member_price && (
                        <div className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <span className="small">Non-Member:</span>
                          <span className="fw-bold">${parseFloat(event.non_member_price).toFixed(2)}</span>
                        </div>
                      )}
                      {event.early_bird_price && (
                        <div className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: '#f0fff0' }}>
                          <span className="small">Early Bird:</span>
                          <span className="fw-bold text-success">${parseFloat(event.early_bird_price).toFixed(2)}</span>
                        </div>
                      )}
                      {event.student_price && (
                        <div className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: '#e7f3ff' }}>
                          <span className="small">Student Price:</span>
                          <span className="fw-bold text-info">${parseFloat(event.student_price).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <h3 className="h6 fw-bold text-dark">Event Type</h3>
                  <p className="text-muted mb-0">{event.event_type}</p>
                </div>

                <div className="mb-3">
                  <h3 className="h6 fw-bold text-dark">Format</h3>
                  <p className="text-muted mb-0">{event.medium}</p>
                </div>

                {event.max_participants && (
                  <div className="mb-3">
                    <h3 className="h6 fw-bold text-dark">Capacity</h3>
                    <p className="text-muted mb-0">Up to {event.max_participants} participants</p>
                  </div>
                )}

                {event.members_only ? (
                  !user && (
                    <div className="alert alert-warning mt-4" role="alert">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Members Only:</strong> This event is exclusively for ARCA members. Please login or <a href="/membership_registration" className="alert-link">become a member</a> to register.
                    </div>
                  )
                ) : (
                  <div className="alert alert-info mt-4" role="alert">
                    <i className="fas fa-globe me-2"></i>
                    <strong>Open to All:</strong> This event is open to everyone. No membership or login required to register.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default EventDetailsPage;